import React, { useMemo } from 'react';
import type { GameOverScene, Scenario } from '../../types';
import { listPriorSceneSelectOptions } from '../../systems/prior-scenes';
import { formatGameOverText } from '../../systems/game-over-text';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

function EditableText({
  value,
  className,
  onChange,
  placeholder,
  multiline = false,
}: {
  value: string;
  className?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    onChange(e.currentTarget.textContent?.trim() ?? '');
  };

  if (multiline) {
    return (
      <div
        className={className}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        data-placeholder={placeholder}
      >
        {value || ''}
      </div>
    );
  }

  return (
    <span
      className={className}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
    >
      {value || placeholder || ''}
    </span>
  );
}

interface InlineGameOverEditorProps {
  item: GameOverScene;
  scenario: Scenario;
  sceneId: string;
  editMode: boolean;
  onChange: (item: GameOverScene) => void;
}

export const InlineGameOverEditor: React.FC<InlineGameOverEditorProps> = ({
  item,
  scenario,
  sceneId,
  editMode,
  onChange,
}) => {
  const priorOptions = useMemo(
    () => listPriorSceneSelectOptions(scenario, sceneId),
    [scenario, sceneId]
  );

  if (!editMode) {
    return (
      <div className={inline.gameOverReadonly}>
        {item.heading && <span className={inline.slText}>{item.heading}</span>}
        <span className={`${inline.slText} ${inline.gameOverText}`}>
          {formatGameOverText(item.text) || '…'}
        </span>
        {item.goto && <span className={inline.choiceDest}>→ {item.goto}</span>}
      </div>
    );
  }

  return (
    <div className={inline.gameOverEditor}>
      <EditableText
        value={item.heading ?? 'GAME OVER'}
        className={inline.gameOverHeading}
        onChange={(heading) => onChange({ ...item, heading: heading || undefined })}
        placeholder="GAME OVER"
      />
      <textarea
        className={inline.gameOverText}
        value={item.text}
        onChange={(e) => onChange({ ...item, text: formatGameOverText(e.target.value) })}
        placeholder="Explication du game over… (Entrée ou \n pour sauter une ligne)"
        rows={5}
      />
      <div className={inline.slMetaRow}>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Recommencer à</span>
          <select
            className={studio.select}
            value={item.goto ?? ''}
            onChange={(e) => onChange({ ...item, goto: e.target.value || undefined })}
          >
            <option value="">— Choisir une scène —</option>
            {priorOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Libellé bouton</span>
          <input
            type="text"
            className={studio.input}
            value={item.restartLabel ?? ''}
            placeholder="Recommencer"
            onChange={(e) =>
              onChange({ ...item, restartLabel: e.target.value.trim() || undefined })
            }
          />
        </div>
      </div>
    </div>
  );
};
